import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    List,
    ListItem,
    Typography, 
    styled,
    ListItemButton,
    ListItemText,
    Container,
} from '@mui/material';
import DrawerItem from './DrawerItem';
import { Link, useLocation } from 'react-router-dom';

// Styled Components
const StyledAppBar = styled(AppBar)(({ scrolled }) => ({
    background: scrolled 
        ? 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)' 
        : 'linear-gradient(135deg, rgba(255, 107, 53, 0.95) 0%, rgba(255, 140, 66, 0.95) 100%)',
    backdropFilter: scrolled ? 'blur(10px)' : 'none',
    boxShadow: scrolled 
        ? '0 4px 30px rgba(255, 107, 53, 0.3)' 
        : 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const StyledToolbar = styled(Toolbar)({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
});

const Logo = styled(Typography)(({ theme }) => ({
    fontFamily: "'Outfit', 'Roboto', sans-serif",
    fontWeight: 800,
    fontSize: '1.5rem',
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.5px',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.2rem',
    },
}));

const ListMenu = styled(List)(({ theme }) => ({
    display: 'none',
    gap: '0.5rem',
    [theme.breakpoints.up('sm')]: {
        display: 'flex',
    },
}));

const NavItem = styled(ListItemButton)(({ active }) => ({
    color: '#fff',
    borderRadius: '8px',
    padding: '0.5rem 1.5rem',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
    '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%) scaleX(0)',
        width: '80%',
        height: '2px',
        backgroundColor: '#fff',
        transition: 'transform 0.3s ease',
    },
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-2px)',
        '&::before': {
            transform: 'translateX(-50%) scaleX(1)',
        },
    },
    '&:active': {
        transform: 'translateY(0)',
    },
}));

const NavText = styled(ListItemText)({
    '& .MuiListItemText-primary': {
        fontWeight: 600,
        fontSize: '0.95rem',
        letterSpacing: '0.5px',
    },
});

// Navigation items
const itemList = [
    { text: 'Home', to: '/' },
    { text: 'About', to: '/about' },
    { text: 'Contact', to: '/contact' }
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrolled]);

    return (
        <StyledAppBar 
            component="nav" 
            position="sticky"
            scrolled={scrolled}
            elevation={0}
        >
            <Container maxWidth="lg">
                <StyledToolbar>
                    <Logo
                        variant="h6"
                        component={Link}
                        to="/"
                    >
                        Perumal Crane Service
                    </Logo>
                    
                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        <DrawerItem />
                    </Box>
                    
                    <ListMenu>
                        {itemList.map((item) => (
                            <ListItem key={item.text} disablePadding>
                                <NavItem 
                                    component={Link} 
                                    to={item.to}
                                    active={location.pathname === item.to ? 1 : 0}
                                >
                                    <NavText primary={item.text} />
                                </NavItem>
                            </ListItem>
                        ))}
                    </ListMenu>
                </StyledToolbar>
            </Container>
        </StyledAppBar>
    );
};

export default Navbar;
